import { ArrowRight, BookOpen, Clock3, RefreshCw, Target } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeading } from "../components/PageHeading";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";
import { DIMENSION_LABELS, formatDate, formatDuration, overdue } from "../lib/format";

export function OverviewPage() {
  const { track, practice } = usePortal();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const [startError, setStartError] = useState<string | null>(null);
  if (!track) return <div className="loading-state">正在读取学习轨道</div>;
  const { summary, current_stage: stage } = track;

  async function start(minutes: number) {
    setStartError(null);
    try {
      const session = await api.startSession(track!.slug, { duration: minutes });
      navigate(`/learn/session/${session.id}`);
    } catch (reason) {
      setStartError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  const metrics = [
    [stage ? String(stage.number).padStart(2, "0") : "--", "当前阶段", stage?.title || "等待规划"],
    [`${summary.evidence_started_count}/${summary.competency_count}`, "已有学习证据", "能力项"],
    [`${summary.mastered_count}/${summary.competency_count}`, "已达掌握门槛", `门槛 ${track.track.mastery_threshold}%`],
    [String(summary.due_review_count), "到期复习", "优先于新内容"],
    [formatDuration(summary.total_minutes), "轨道活动记录", "包含资料建设"],
  ];

  return (
    <>
      <PageHeading number="01" title={track.track.title} description={track.track.goal} />

      <section className="metric-strip" aria-label="学习概况">
        {metrics.map(([value, label, detail], index) => (
          <div className={`metric-cell ${index === 3 && summary.due_review_count ? "is-alert" : ""}`} key={label}>
            <strong>{value}</strong><span>{label}</span><small>{detail}</small>
          </div>
        ))}
      </section>

      <section className="current-action section-block">
        <div className="section-title-row">
          <div><span className="section-number">01.1</span><h2>现在应该做什么</h2></div>
          <BookOpen size={24} />
        </div>
        <p className="lead-copy">{track.latest_next_action || track.curriculum.position_note}</p>
        {startError && <p className="form-error" role="alert">{startError}</p>}
        <div className="time-actions">
          {track.curriculum.time_modes.length ? track.curriculum.time_modes.map((mode) => (
            <button key={mode.minutes} className="time-button" type="button" disabled={!capabilities.practice.writable} onClick={() => void start(mode.minutes)}>
              <span><Clock3 size={17} />{mode.minutes} 分钟</span>
              <small>{mode.purpose}</small>
              <ArrowRight size={18} />
            </button>
          )) : <div className="empty-state">当前轨道尚未声明按时长启动的任务，请从训练中心选择学习单元。</div>}
        </div>
      </section>

      <div className="two-column-grid section-block">
        <section>
          <div className="section-title-row"><div><span className="section-number">01.2</span><h2>到期复习</h2></div><RefreshCw size={22} /></div>
          <div className="lined-list">
            {track.due_reviews.length ? track.due_reviews.map((review) => (
              <div className="review-row" key={`${review.competency_id}-${review.dimension}`}>
                <div><strong>{review.competency_name}</strong><span>{DIMENSION_LABELS[review.dimension] || review.dimension} · {formatDate(review.next_review)}</span></div>
                <b>{overdue(review.next_review)}</b>
              </div>
            )) : <div className="empty-state">当前没有到期复习。</div>}
          </div>
        </section>
        <section>
          <div className="section-title-row"><div><span className="section-number">01.3</span><h2>网页训练记录</h2></div><Target size={22} /></div>
          <div className="practice-summary">
            <div><strong>{practice?.completed_sessions || 0}</strong><span>完成训练</span></div>
            <div><strong>{practice?.answered_items || 0}</strong><span>已回答题目</span></div>
            <div><strong>{practice?.pending_reviews || 0}</strong><span>等待 Codex 复核</span></div>
          </div>
          <p className="method-note">阅读与引导练习只记录接触；只有未见、确定性判分且达到门槛的测试才进入正式证据。</p>
        </section>
      </div>

      <section className="section-block">
        <div className="section-title-row"><div><span className="section-number">01.4</span><h2>最近测评证据</h2></div></div>
        <div className="assessment-table">
          {track.assessments.slice(0, 5).map((assessment) => (
            <div className="assessment-row" key={assessment.id}>
              <strong className={assessment.passed ? "score-pass" : "score-fail"}>{assessment.score.toFixed(0)}</strong>
              <div><b>{assessment.competency_name}</b><span>{DIMENSION_LABELS[assessment.dimension] || assessment.dimension} · {formatDate(assessment.occurred_at, true)}</span></div>
              <p>{assessment.notes || "未记录反馈"}</p>
              <span className={`result-label ${assessment.passed ? "pass" : "fail"}`}>{assessment.passed ? "通过" : "未通过"}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
